import { logWarn } from './logger';

/**
 * Modul-Singleton für die Marketing-Einwilligung (Cookie-Consent-Banner /
 * Google Ads Consent Mode v2).
 *
 * Muster wie installPrompt.js: modul-globaler Zustand + subscribe/notify,
 * damit mehrere Konsumenten (CookieConsentBanner, InstallAppBanner,
 * PrivacyPage) ohne gemeinsamen React-Kontext denselben Stand sehen.
 *
 * @module utils/consentStore
 */

/** localStorage-Schlüssel für die persistierte Einwilligungsentscheidung. */
export const CONSENT_STORAGE_KEY = 'rentinger.consent.v1';

/**
 * Version des Einwilligungstexts. Bei einer materiellen Änderung der
 * Cookie-Banner-Formulierung (z.B. neuer Zweck, neuer Empfänger) muss diese
 * Zahl erhöht werden — dadurch behandelt readPersistedRecord() eine unter
 * der alten Version gespeicherte Entscheidung automatisch als
 * "unentschieden" (null), sodass die Einwilligung erneut eingeholt wird.
 */
export const CONSENT_VERSION = 1;

// Modul-Cache: vermeidet localStorage-Zugriffe bei jedem Render.
// undefined = noch nicht aus dem Storage gelesen; danach { status, timestamp } | null.
let cachedRecord;

const subscribers = new Set();

let storageListenerAttached = false;

const parsePersistedRecord = (raw) => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) {
      return null;
    }
    if (parsed.status !== 'granted' && parsed.status !== 'denied') {
      return null;
    }
    return { status: parsed.status, timestamp: parsed.timestamp || null };
  } catch (e) {
    return null;
  }
};

const notifySubscribers = () => {
  const current = getMarketingConsent();
  subscribers.forEach((callback) => {
    try {
      callback(current);
    } catch (e) {
      // Ein fehlerhafter Subscriber darf die anderen nicht blockieren.
    }
  });
};

const handleStorageChange = (event) => {
  if (event.storageArea && event.storageArea !== window.localStorage) {
    return;
  }
  // localStorage.clear() meldet key === null. Auch dann darf ein anderer Tab
  // keinen zuvor gecachten Consent-Stand behalten.
  if (event.key !== CONSENT_STORAGE_KEY && event.key !== null) {
    return;
  }
  cachedRecord = event.key === null ? null : parsePersistedRecord(event.newValue);
  notifySubscribers();
};

const attachStorageListener = () => {
  if (!storageListenerAttached) {
    window.addEventListener('storage', handleStorageChange);
    storageListenerAttached = true;
  }
};

const detachStorageListenerIfUnused = () => {
  if (storageListenerAttached && subscribers.size === 0) {
    window.removeEventListener('storage', handleStorageChange);
    storageListenerAttached = false;
  }
};

/**
 * Liest den gespeicherten Einwilligungsdatensatz aus localStorage.
 *
 * Kaputtes JSON, eine abweichende `version` oder ein unbekannter `status`
 * werden bewusst als "unentschieden" (null) behandelt — z.B. damit eine
 * materielle Textänderung (CONSENT_VERSION hochgezählt) die Einwilligung
 * beim nächsten Seitenaufruf erneut einholt.
 *
 * @returns {{status: 'granted'|'denied', timestamp: string|null}|null}
 */
const readPersistedRecord = () => {
  try {
    return parsePersistedRecord(window.localStorage.getItem(CONSENT_STORAGE_KEY));
  } catch (e) {
    // localStorage kann in Capacitor-/Privacy-Umgebungen werfen (vgl.
    // InstallAppBanner.js). In diesem Fall gilt die Einwilligung nur für die
    // laufende Sitzung — cachedRecord bleibt danach der In-Memory-Fallback,
    // ein erneuter Lesezugriff wird nicht mehr versucht (cachedRecord ist
    // nach diesem Aufruf nicht mehr undefined).
    logWarn('[consentStore] localStorage nicht lesbar, Fallback auf Sitzungsspeicher.', e);
    return null;
  }
};

const getRecord = () => {
  if (cachedRecord === undefined) {
    cachedRecord = readPersistedRecord();
  }
  return cachedRecord;
};

/**
 * Liefert den aktuellen Stand der Marketing-Einwilligung.
 * null bedeutet "noch keine Entscheidung getroffen".
 *
 * @returns {'granted'|'denied'|null}
 */
export const getMarketingConsent = () => {
  const record = getRecord();
  return record ? record.status : null;
};

/**
 * Liefert den ISO-Zeitstempel der letzten Einwilligungsentscheidung, oder
 * null, wenn noch keine Entscheidung getroffen wurde. Wird für die Anzeige
 * "erteilt am / abgelehnt am" in der Datenschutzerklärung benötigt.
 *
 * @returns {string|null}
 */
export const getMarketingConsentTimestamp = () => {
  const record = getRecord();
  return record ? record.timestamp : null;
};

/**
 * Setzt und persistiert den Einwilligungsstatus.
 *
 * Der Zeitstempel erfüllt die Nachweispflicht nach Art. 7 Abs. 1 DSGVO: Der
 * Verantwortliche muss nachweisen können, WANN unter WELCHER Textversion
 * (CONSENT_VERSION) eine Einwilligung erteilt oder widerrufen wurde.
 *
 * @param {'granted'|'denied'} status
 */
export const setMarketingConsent = (status) => {
  if (status !== 'granted' && status !== 'denied') {
    throw new TypeError(`Ungueltiger Marketing-Consent-Status: ${status}`);
  }
  const timestamp = new Date().toISOString();
  // Cache sofort aktualisieren — unabhängig davon, ob die Persistierung
  // unten gelingt (In-Memory-Fallback für die laufende Sitzung).
  cachedRecord = { status, timestamp };
  try {
    const persisted = { status, version: CONSENT_VERSION, timestamp };
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(persisted));
  } catch (e) {
    logWarn('[consentStore] Einwilligung konnte nicht persistiert werden, gilt nur für diese Sitzung.', e);
  }
  notifySubscribers();
};

/**
 * Abonniert Änderungen am Einwilligungsstatus.
 * Der Callback wird SOFORT mit dem aktuellen Stand aufgerufen (Muster wie
 * installPrompt.js subscribeInstallPrompt()).
 *
 * MINOR-7-Fix: Der synchrone Erstaufruf ist — analog zu notifySubscribers()
 * oben — in try/catch gekapselt. CookieConsentBanner, InstallAppBanner und
 * PrivacyPage abonnieren alle beim Mount; ein werfender Erstaufruf hätte
 * ohne diesen Schutz die Registrierung ALLER nachfolgenden Subscriber
 * verhindert (subscribers.add() lief zwar bereits vorher, aber ein
 * unbehandelter Wurf hier würde den Aufrufer aus seinem eigenen
 * Mount-Effect werfen und die Ausführung des restlichen Effects abbrechen).
 *
 * @param {(status: 'granted'|'denied'|null) => void} callback
 * @returns {() => void} Unsubscribe-Funktion
 */
export const subscribeConsent = (callback) => {
  subscribers.add(callback);
  attachStorageListener();
  try {
    callback(getMarketingConsent());
  } catch (e) {
    // Ein fehlerhafter Subscriber darf die Registrierung nicht verhindern.
  }
  return () => {
    subscribers.delete(callback);
    detachStorageListenerIfUnused();
  };
};
